import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Divider,
    FormControlLabel,
    FormGroup,
    Grid,
    Typography
} from '@mui/material';
import { getAllowedMerchantSettings, updateMerchantSettings } from 'services/merchantServices';
import { toast } from 'react-toastify';
import { TCLabel, TCTitleLabel } from 'components/TopCourtComponents/Label/TopCourtLabel';
import { getPlaces } from 'services/placeServices';
import { getFirstLetters } from 'helper/String';
import { setCourtPlace } from 'helper/LocalStorage';

const ALL_PERMISSIONS = [
    { key: 'dashboard', label: 'Dashboard' },
    { key: 'order', label: 'Order' },
    { key: 'payment', label: 'Payment' },

    // Marketing
    { key: 'value_pack', label: 'Value Pack' },
    { key: 'promo_code', label: 'Promo Code' },
    { key: 'voucher', label: 'Voucher' },

    // Others
    { key: 'add_on', label: 'Add-On' },
    { key: 'classes', label: 'Class & Event' },

    // Settings
    { key: 'settings_court', label: 'Court' },
    { key: 'settings_rain_refund', label: 'Rain Refund' },
    { key: 'settings_category', label: 'Category' },
    { key: 'settings_txn_fee', label: 'Txn Fee' },
    { key: 'settings_tnc', label: 'T&C' },
    { key: 'settings_admin', label: 'Admin' },
    // { key: 'menu', label: 'Menu' },
];

const GROUPS = [
    { title: 'Core', keys: ['dashboard', 'order', 'payment'] },
    { title: 'Marketing', keys: ['value_pack', 'promo_code', 'voucher'] },
    { title: 'Others', keys: ['add_on', 'classes'] },
    { title: 'Settings', keys: ['settings_court', 'settings_rain_refund', 'settings_category', 'settings_txn_fee', 'settings_tnc', 'settings_admin'] }
];

function MerchantMenu({ merchantId, allowedMenu }) {
    const [selected, setSelected] = useState([]);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (!merchantId) return;
    }, [merchantId]);

    const selectedSet = useMemo(() => new Set(selected), [selected]);
    let permissionInfo = JSON.parse(localStorage.getItem("user_info")) || null

    const toggleOne = (key) => {
        setSelected((prev) => {
            const s = new Set(prev);
            if (s.has(key)) s.delete(key);
            else s.add(key);
            return Array.from(s);
        });
    };

    useEffect(() => {
        setSelected(allowedMenu)
    }, [allowedMenu])

    const isGroupAllChecked = (keys) => keys.every((k) => selectedSet.has(k));
    const isGroupIndeterminate = (keys) => keys.some((k) => selectedSet.has(k)) && !isGroupAllChecked(keys);

    const toggleGroup = (keys) => {
        setSelected((prev) => {
            const s = new Set(prev);
            const allChecked = keys.every((k) => s.has(k));

            if (allChecked) {
                keys.forEach((k) => s.delete(k));
            } else {
                keys.forEach((k) => s.add(k));
            }
            return Array.from(s);
        });
    };

    const selectAll = () => setSelected(ALL_PERMISSIONS.map((p) => p.key));
    const clearAll = () => setSelected([]);

    const onSave = async () => {
        try {
            setSaving(true);
            let { data, error } = await updateMerchantSettings(merchantId, { allowedMenu: selected })
            if (data) {
                setSaving(false);
                toast.success("Update menu success");
                loadPlace()
            }
        } catch (e) {
            toast.error(e);
        } finally {
            setSaving(false);
        }
    };

    const loadPlace = async () => {
        let { data, error } = await getPlaces({
            isActive: true
        });
        if (error) { console.log(error) }
        if (data) {
            let menu = await getListMenu()
            let court = data.map(value => ({
                id: value._id,
                name: value.name,
                code: getFirstLetters(value.name),
                merchantId: value.merchantId || '',
                allowedMenu: menu ? menu.find(item => item.merchantId === value.merchantId)?.allowedMenu : []
            }))

            localStorage.setItem("court_place", JSON.stringify(court))
            court = court.find(value => value.merchantId === merchantId)
            if (permissionInfo.merchantInfo && court) {
                setCourtPlace(court);
                permissionInfo.merchantInfo.setting.allowedMenu = court.allowedMenu
                localStorage.setItem("user_info", JSON.stringify(permissionInfo))
            } else if (court) {
                localStorage.setItem("allowed_menu", JSON.stringify(court.allowedMenu))
                window.location.reload()
            }
        }
    }

    const getListMenu = async () => {
        let { data, error } = await getAllowedMerchantSettings()
        if (error) { console.log(error) }
        if (data) {
            return data
        }
    };

    const permLabel = (key) => ALL_PERMISSIONS.find((p) => p.key === key)?.label ?? key;

    return (
        <Box>
            <Card style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }} >
                <CardContent>
                    <Grid container row="true" display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Grid item xs={12} md={4} padding={1}>
                            <TCTitleLabel variant="h4">Merchant Menu Permissions</TCTitleLabel>
                            {/* <Typography variant="body2" color="text.secondary">
                                Merchant ID: {merchantId ?? '-'}
                            </Typography> */}
                        </Grid>

                        <Grid item xs={12} md={8} display="flex" gap={1} justifyContent="flex-end">
                            <Button sx={{ border: '0.5px solid #2196f3', borderRadius: 4 }} variant="outlined" onClick={clearAll} disabled={saving}>
                                Clear
                            </Button>
                            <Button sx={{ border: '0.5px solid #2196f3', borderRadius: 4 }} variant="outlined" onClick={selectAll} disabled={saving}>
                                Select All
                            </Button>
                            <Button variant="contained" onClick={onSave} disabled={saving || !merchantId}>
                                {saving ? 'Saving...' : 'Save'}
                            </Button>
                        </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                        {GROUPS.map((g) => (
                            <Grid item xs={12} md={4} key={g.title}>
                                <Card variant="outlined">
                                    <Grid p={2} pt={1}>

                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <Typography variant="h6">{g.title}</Typography>

                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={isGroupAllChecked(g.keys)}
                                                        indeterminate={isGroupIndeterminate(g.keys)}
                                                        onChange={() => toggleGroup(g.keys)}
                                                    />
                                                }
                                                label="All"
                                            />
                                        </Box>

                                        <Divider sx={{ mb: 1 }} />

                                        <FormGroup>
                                            {g.keys.map((key) => (
                                                <FormControlLabel
                                                    key={key}
                                                    control={<Checkbox checked={selectedSet.has(key)} onChange={() => toggleOne(key)} />}
                                                    label={permLabel(key)}
                                                />
                                            ))}
                                        </FormGroup>
                                    </Grid>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>
        </Box>
    );
}

export default MerchantMenu;
